import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listCategories, createCategory, updateCategory, deleteCategory, Category } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const Categories: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    listCategories(sessionToken)
      .then(res => setCategories(res.categories))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ name: '', description: '' }); setEditId(null); setShowForm(false); };

  const handleEdit = (c: Category) => {
    setForm({ name: c.name, description: c.description || '' });
    setEditId(c.id);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editId) {
        await updateCategory(sessionToken, editId, form);
      } else {
        await createCategory(sessionToken, form);
      }
      resetForm();
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deactivate this category?')) return;
    try {
      await deleteCategory(sessionToken, id);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="inv2-loading">Loading...</div>;
  const canWrite = ['Owner', 'Admin', 'Manager'].includes(user?.role || '');

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <h1>🏷️ Inventory Categories</h1>
        <div className="inv2-header-actions">
          {canWrite && <button onClick={() => { resetForm(); setShowForm(true); }} className="inv2-btn inv2-btn-primary">+ Add Category</button>}
        </div>
      </div>

      {error && <div className="inv2-error">{error}</div>}

      {showForm && (
        <div className="inv2-modal-overlay" onClick={resetForm}>
          <div className="inv2-modal" onClick={e => e.stopPropagation()}>
            <h2>{editId ? 'Edit Category' : 'New Category'}</h2>
            <form onSubmit={handleSubmit} className="inv2-form">
              <div className="inv2-form-group"><label>Name *</label><input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="inv2-input" /></div>
              <div className="inv2-form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="inv2-textarea" /></div>
              <div className="inv2-form-actions">
                <button type="submit" className="inv2-btn inv2-btn-primary">{editId ? 'Update' : 'Create'}</button>
                <button type="button" onClick={resetForm} className="inv2-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="inv2-table-container">
        <DataTable
          rows={categories}
          rowKey={(c) => c.id}
          caption="Categories"
          moreLabel="Description"
          empty="No categories yet"
          rowClass={(c) => (!c.is_active ? 'inv2-row-inactive' : undefined)}
          columns={[
            { key: 'name', header: 'Name', priority: 'primary', render: (c) => c.name },
            { key: 'items', header: 'Items', priority: 'summary', numeric: true, render: (c) => c.item_count || 0 },
            { key: 'status', header: 'Status', priority: 'summary', render: (c) => (c.is_active ? <span className="inv2-badge-active">Active</span> : <span className="inv2-badge-inactive">Inactive</span>) },
            { key: 'desc', header: 'Description', priority: 'detail', render: (c) => c.description || '—' },
          ]}
          actions={canWrite ? (c) => (
            <>
              <button onClick={() => handleEdit(c)} className="inv2-btn inv2-btn-sm">Edit</button>
              {c.is_active && user?.role === 'Owner' && (
                <button onClick={() => handleDelete(c.id)} className="inv2-btn inv2-btn-sm inv2-btn-danger">Deactivate</button>
              )}
            </>
          ) : undefined}
        />
      </div>
    </div>
  );
};

export default Categories;
